import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { APPLICATION_STEPS, PRIMERS } from '../config/products';
import { cn } from '../lib/utils';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Eraser, Sparkles, Paintbrush2, Paintbrush, Clock, Layers, Shield, Droplets,
  AlertCircle, Lightbulb, ChevronRight, ChevronDown, ChevronUp, Play, AlertTriangle
} from 'lucide-react';

const iconMap = {
  Eraser: Eraser,
  Sparkles: Sparkles,
  Paintbrush2: Paintbrush2,
  Paintbrush: Paintbrush,
  Clock: Clock,
  Layers: Layers,
  Shield: Shield,
  Droplets: Droplets,
};

export const ApplicationGuide = ({ includeSealer, substrateDetails, textureType }) => {
  const { t, isRTL } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  // Get appropriate steps based on substrate and texture
  let steps = Object.values(APPLICATION_STEPS);
  if (!includeSealer) {
    steps = steps.filter(step => step.id !== 'sealing');
  }

  // Determine primer type based on substrate
  const primerType = substrateDetails?.absorbent ? 'solan' : 'quartz';
  const primerInfo = PRIMERS[primerType];
  
  // Determine MicroMineral type based on texture
  const microlitType = textureType === 'textured' ? 'thick' : 'diluted';

  // Customize step descriptions based on selections
  const getCustomizedStep = (step) => {
    if (step.id === 'primer') {
      return {
        ...step,
        titleHe: primerInfo?.nameHe || step.titleHe,
        titleEn: primerInfo?.nameEn || step.titleEn,
        descriptionHe: substrateDetails?.absorbent 
          ? 'מרח שכבה אחידה של פריימר סולן לתשתית סופגת. הפריימר יוצר בסיס מושלם להידבקות.'
          : 'מרח שכבה אחידה של פריימר קוורץ לתשתית לא סופגת. הפריימר יוצר אחיזה מכנית למשטח.',
        descriptionEn: substrateDetails?.absorbent 
          ? 'Apply an even layer of Solan primer for absorbent surface. The primer creates a perfect base for adhesion.'
          : 'Apply an even layer of Quartz primer for non-absorbent surface. The primer creates mechanical grip on the surface.',
      };
    }
    
    if (step.id === 'firstCoat' || step.id === 'finalCoat') {
      return {
        ...step,
        titleHe: step.id === 'firstCoat' 
          ? `שכבה ראשונה - מיקרומינרל ${textureType === 'textured' ? 'סמיך' : 'דליל'}`
          : `שכבת גמר - מיקרומינרל ${textureType === 'textured' ? 'סמיך' : 'דליל'}`,
        titleEn: step.id === 'firstCoat'
          ? `First Coat - MicroMineral ${textureType === 'textured' ? 'Thick' : 'Diluted'}`
          : `Final Coat - MicroMineral ${textureType === 'textured' ? 'Thick' : 'Diluted'}`,
      };
    }
    
    return step;
  };

  return (
    <div className="space-y-4">
      {/* Collapsible Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            'w-full h-auto py-4 px-6 justify-between text-start',
            isExpanded && 'bg-accent/5 border-accent/30'
          )}
        >
          <div className="flex items-center gap-4">
            <motion.div 
              className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center"
              animate={{ rotate: isExpanded ? 0 : [0, -5, 5, 0] }}
              transition={{ duration: 0.5, repeat: isExpanded ? 0 : Infinity, repeatDelay: 3 }}
            >
              <Play className="w-5 h-5 text-accent" />
            </motion.div>
            <div>
              <h3 className="font-semibold text-lg text-foreground">
                {isRTL ? 'תהליך יישום מפורט' : 'Detailed Application Process'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isRTL 
                  ? `${steps.length} שלבים • ${primerInfo?.nameHe || 'פריימר'} + מיקרומינרל ${textureType === 'textured' ? 'סמיך' : 'דליל'}`
                  : `${steps.length} steps • ${primerInfo?.nameEn || 'Primer'} + MicroMineral ${textureType === 'textured' ? 'Thick' : 'Diluted'}`
                }
              </p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown className="w-6 h-6 text-muted-foreground" />
          </motion.div>
        </Button>
      </motion.div>

      {/* Important Note - Always visible */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="p-4 rounded-xl bg-warning/5 border border-warning/20 flex items-start gap-3"
      >
        <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-foreground">
            {isRTL ? 'חשוב לפני יישום שכבת גמר:' : 'Important before final coat:'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {isRTL 
              ? 'יש לוודא שאין סדיקה או הפרדה. במקרה שיש - יש לשייף ולתקן עם אותו החומר בשכבות דקות.'
              : 'Make sure there are no cracks or separation. If there are - sand and repair with the same material in thin layers.'
            }
          </p>
        </div>
      </motion.div>

      {/* Expandable Steps */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="space-y-4 overflow-hidden"
          >
            {steps.map((step, index) => {
              const customizedStep = getCustomizedStep(step);
              const Icon = iconMap[step.icon] || Sparkles;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                >
                  <Card className={cn(
                    'overflow-hidden transition-all duration-300',
                    step.critical && 'border-warning/30 bg-warning/5',
                    step.optional && 'border-dashed opacity-90'
                  )}>
                    <CardContent className="p-0">
                      <div className="flex items-stretch">
                        {/* Step Number */}
                        <div className={cn(
                          'w-16 sm:w-20 flex-shrink-0 flex flex-col items-center justify-center py-4',
                          step.critical 
                            ? 'bg-warning/10' 
                            : step.optional 
                              ? 'bg-muted/50'
                              : 'bg-accent/5'
                        )}>
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            className={cn(
                              'w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-base sm:text-lg font-bold',
                              step.critical 
                                ? 'bg-warning text-warning-foreground' 
                                : step.optional 
                                  ? 'bg-muted text-muted-foreground'
                                  : 'bg-accent text-accent-foreground'
                            )}
                          >
                            {index + 1}
                          </motion.div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-4 sm:p-5">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className={cn(
                                'w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center',
                                step.critical 
                                  ? 'bg-warning/10 text-warning' 
                                  : step.optional 
                                    ? 'bg-muted text-muted-foreground'
                                    : 'bg-secondary text-accent'
                              )}>
                                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-foreground text-sm sm:text-base">
                                  {isRTL ? customizedStep.titleHe : customizedStep.titleEn}
                                </h3>
                                <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                                  <Clock className="w-3 h-3" />
                                  {isRTL ? step.durationHe : step.durationEn}
                                </div>
                              </div>
                            </div>

                            {/* Badges */}
                            <div className="flex gap-1 flex-shrink-0">
                              {step.critical && (
                                <Badge className="bg-warning/10 text-warning border-warning/30 text-[10px] px-1.5">
                                  <AlertCircle className="w-3 h-3 me-0.5" />
                                  {isRTL ? 'חשוב' : 'Important'}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Description */}
                          <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                            {isRTL ? customizedStep.descriptionHe : customizedStep.descriptionEn}
                          </p>

                          {/* Tips */}
                          {step.tips && (
                            <div className="p-3 rounded-lg bg-secondary/50">
                              <div className="flex items-center gap-2 mb-1.5">
                                <Lightbulb className="w-3.5 h-3.5 text-accent" />
                                <span className="text-xs font-medium">{isRTL ? 'טיפים' : 'Tips'}</span>
                              </div>
                              <ul className="space-y-1">
                                {(isRTL ? step.tips.he : step.tips.en).map((tip, i) => (
                                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                    <ChevronRight className="w-3 h-3 text-accent flex-shrink-0 mt-0.5" />
                                    {tip}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
